import { memo, useMemo } from 'react';
import { BulkActionBar } from '@aimsos/ui';
import { Send, Trash2, UserX } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';

import { usePermissions } from '../../hooks/usePermission';
import { MEMBER_BULK_ACTION } from '../../constants/memberActions';
import { getBulkActions } from '../../utils/memberActionRules';

const BULK_ICON = Object.freeze({
  [MEMBER_BULK_ACTION.SEND_INVITATIONS]: Send,
  [MEMBER_BULK_ACTION.SUSPEND]: UserX,
  [MEMBER_BULK_ACTION.REMOVE]: Trash2,
});

/**
 * Bulk bar over the selected Members ("N members selected"): actions come from `getBulkActions`
 * (Send invitations, Suspend, Remove in red), each disabled with its reason when it cannot apply
 * to the whole selection.
 *
 * @param {object} props
 * @param {object[]} props.members Selected UI members.
 * @param {(actionId: string) => void} props.onAction
 * @param {() => void} props.onClear
 * @param {boolean} [props.disabled] An action is running: every button is locked.
 */
const MembersBulkBar = ({ members, onAction, onClear, disabled = false }) => {
  const { t } = useTranslation('people-access');
  const { actor, mode } = usePermissions();

  const actions = useMemo(
    () =>
      getBulkActions(members, actor, { mode }).map((action) => ({
        id: action.id,
        label: t(action.labelKey),
        variant: action.variant,
        icon: BULK_ICON[action.id],
        disabled: disabled || !action.enabled,
        disabledReason: action.disabledReasonKey ? t(action.disabledReasonKey) : undefined,
        onClick: () => onAction(action.id),
      })),
    [members, actor, mode, t, onAction, disabled],
  );

  if (!members.length) return null;

  return (
    <BulkActionBar
      selectedLabel={t('members.bulk.selected', { count: members.length })}
      actions={actions}
      onClear={onClear}
      actionsAlign="start"
      clearLabel={t('members.bulk.clear')}
    />
  );
};

export default memo(MembersBulkBar);
